
import Vue, { PropType } from 'vue';
import uniq from 'lodash/uniq';
import { RouteMixin } from '@/mixins';
import { ApplicationNavigationUtils } from '@/utils';
import { LoanApplication, ApplicationTemplateMetadata, UsableProducts, Permission, FeatureFlagging } from '@sageworks/jpi';
import { getProposedProductName } from '@/utils/proposed-products/proposed-product-helper';
import { OnlinePortalRoutes } from '@/OnlinePortalRoutes';
import { RemoveLoanModal } from '@/components/remove-loan-modal';
import { NewLoanRequestModal } from '@/components/new-loan-request-modal';
import { LoanApplicationMetadataModuleState } from '../../store/LoanApplicationMetadataModule';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { mapGetters } from 'vuex';
import { isInFinalStatus as isApplicationInFinalStatus } from '../../utils/application-final-status-helper/application-final-status-helper';
import { buildModernPortalApplicationUrl } from '../../utils/PortalSwitchUtils';

export default Vue.extend({
	name: 'ApplicationWizardNavigation',
	components: { RemoveLoanModal, NewLoanRequestModal, FontAwesomeIcon },
	mixins: [RouteMixin],
	props: {
		applicationId: {
			type: Number,
			required: false,
			default: 0
		},
		navigationMetadata: {
			type: Array as PropType<{ id: string; label: string }[]>,
			required: false,
			default: () => {
				return [];
			}
		},
		pageIndex: {
			type: Number,
			required: true
		}
	},
	data() {
		return {
			subNavItemCssClass: 'pl-5 font-weight-normal dynamic-forms-wizard-nav-item dynamic-forms-wizard-active-product',
			baseNavItemCssClass: 'dynamic-forms-wizard-nav-item',
			activeNavItemCssClass: 'dynamic-forms-wizard-active-page',
			isSwitchToNewPortalEnabled: false
		};
	},
	computed: {
		...mapGetters('InstitutionSettings', ['allowUserToAddRemoveLoans', 'allowBorrowerToAddRemoveLoans']),
		...mapGetters('LoanApplicationMetadata', ['loanRequiresReview']),
		activeRouteName(): string {
			return this.$route.name ?? '';
		},
		metadataList(): ApplicationTemplateMetadata[] {
			return this.directStore.state.LoanApplicationMetadata.metadata;
		},
		currentLoanIndex(): number {
			return this.directStore.state.MultiLoanApplicationForm.loanIndex;
		},
		activeMetadata(): ApplicationTemplateMetadata | undefined {
			const { metadata } = this.directStore.state.LoanApplicationMetadata as LoanApplicationMetadataModuleState;
			return metadata[this.currentLoanIndex];
		},
		isApplicationRoute(): boolean {
			return OnlinePortalRoutes.ApplicationForm.name === this.activeRouteName;
		},
		allowEntityToAddRemoveLoans(): boolean {
			if (this.applicationType === LoanApplication.TypeEnum.Sba) {
				return false;
			}
			if (this.directStore.getters.User.isLender) {
				return this.allowUserToAddRemoveLoans;
			}
			return this.allowBorrowerToAddRemoveLoans;
		},
		applicationType(): LoanApplication.TypeEnum | null {
			return this.directStore.state.LoanApplicationData.loanApplication?.type ?? null;
		},
		maxLoansPerApplication(): number {
			return this.directStore.state.InstitutionSettings.maxLoansPerApplication;
		},
		canAddNewLoan() {
			return (
				this.allowEntityToAddRemoveLoans &&
				this.applicationType &&
				this.directStore.state.LoanApplicationMetadata.metadata.length < this.maxLoansPerApplication
			);
		},
		authToken(): string | null {
			return this.directStore.state.Authentication.userToken;
		},
		canSwitchToNewPortal(): boolean {
			return this.isSwitchToNewPortalEnabled && !!this.authToken;
		},
		formattedProposedProducts() {
			const validationResults = this.directStore.state.LoanApplicationData.validationResults?.results ?? {};

			return this.directStore.state.LoanApplicationMetadata.metadata
				.filter(x => x.proposedProduct && x.loanMappingId)
				.map(metadata => ({
					name: this.getProductName(metadata.proposedProduct!),
					loanMappingId: metadata.loanMappingId!,
					isValid: validationResults[metadata.loanMappingId!]?.isValid ?? true
				}));
		},
		activeProductPages(): { id: string; label: string; isValid: boolean; errors: string[] }[] {
			const validationResult = this.directStore.state.LoanApplicationData.validationResults?.results ?? {};
			const pages = this.navigationMetadata;

			if (!this.activeMetadata) return [];

			const { isValid, errors } = validationResult[this.activeMetadata.loanMappingId] ?? { isValid: true, errors: [] };

			return pages.map(x => {
				const pageErrors = errors?.filter(({ componentPath }) => componentPath && componentPath.length > 0 && componentPath[0] === x.id) ?? [];

				return {
					id: x.id,
					label: x.label,
					isValid: isValid ? true : pageErrors.length === 0,
					errors: uniq(pageErrors.map(error => error.message) as string[])
				};
			});
		},
		applicationStatus(): LoanApplication.ApplicationStatusEnum | null {
			return this.directStore.state.LoanApplicationData.loanApplication?.applicationStatus ?? null;
		},
		canSubmitBeforeLenderReview(): boolean {
			return this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.SubmitApplicationBeforeLenderReview);
		},
		canSubmitApplicationInBorrowerReview(): boolean {
			return this.directStore.getters.User.isLender
				? this.directStore.getters.LenderAuthorizationModule.canAccessFeature(Permission.ProductFeatureEnum.SubmitApplicationInBorrowerReview)
				: true;
		},
		isInFinalStatus(): boolean {
			return isApplicationInFinalStatus(this.applicationStatus ?? LoanApplication.ApplicationStatusEnum.New, this.canSubmitApplicationInBorrowerReview);
		},
		finalTabTitle(): string {
			if (!this.isInFinalStatus && !this.canSubmitBeforeLenderReview && this.loanRequiresReview) {
				return 'Review';
			}
			return 'Submit ' + this.itemLabel;
		},
		itemLabel(): string {
			if (this.hasCommunityLendingSubscription === undefined) {
				return '';
			}
			return this.hasCommunityLendingSubscription ? 'Request Form' : 'Application';
		},
		hasCommunityLendingSubscription(): boolean | undefined {
			return this.directStore.state.UsableProducts.usableProducts?.has('abrigoCommunityLending' as UsableProducts.ProductsEnum);
		}
	},
	async mounted() {
		await Promise.all([
			this.directStore.dispatch.UsableProducts.fetchUsableProducts(),
			this.loadSwitchToNewPortalFeatureFlag()
		]);
	},
	methods: {
		getProductName: getProposedProductName,
		async loadSwitchToNewPortalFeatureFlag(): Promise<void> {
			this.isSwitchToNewPortalEnabled = await this.directStore.dispatch.FeatureFlag.fetchIsFeatureFlagActive(
				FeatureFlagging.FeatureFlagEnum.EnableModernOpnAppToggle
			);
		},
		getNavItemClass(routeName: string): string {
			let output = this.baseNavItemCssClass;
			if (routeName === this.activeRouteName) {
				output += ' ' + this.activeNavItemCssClass;
			}
			return output;
		},
		getProductClass(loanMappingId: number): string {
			let output = this.baseNavItemCssClass;
			if (loanMappingId === this.activeMetadata?.loanMappingId && this.isApplicationRoute) {
				output += ' dynamic-forms-wizard-active-product dynamic-forms-wizard-active-product-name';
			}
			return output;
		},
		getSubmitButtonName(): string {
			return OnlinePortalRoutes.ApplicationSubmit.name;
		},
		async navigateToApplication(loanMappingId: number): Promise<void> {
			const { metadata } = this.directStore.state.LoanApplicationMetadata as LoanApplicationMetadataModuleState;
			const loanIndex = metadata.findIndex(x => x.loanMappingId === loanMappingId);
			const newIndex = loanIndex !== this.currentLoanIndex;
			if (newIndex) {
				this.$root.$emit('change-loan', loanIndex);
			}
			if (!this.isApplicationRoute) {
				await this.directStore.dispatch.MultiLoanApplicationForm.updateLoanIndex({ loanIndex: loanIndex });
				ApplicationNavigationUtils.navigateToApplicationForm(this.$router, this.applicationId);
			}
		},
		navigateToDocumentation(): void {
			ApplicationNavigationUtils.navigateToApplicationDocumentation(this.$router, this.applicationId);
		},
		navigateToSubmit(): void {
			ApplicationNavigationUtils.navigateToApplicationSubmit(this.$router, this.applicationId);
		},
		switchToNewPortal(): void {
			if (!this.authToken) {
				return;
			}

			window.location.assign(buildModernPortalApplicationUrl(this.applicationId, { token: this.authToken }));
		},
		navigateToPage(index: Number, isValid: boolean): void {
			this.$root.$emit('navigate-to-page', { index: index, isValid: isValid, previousPageId: this.activeProductPages[this.pageIndex].id });
		}
	}
});
